import React, { useCallback, useMemo, useState } from 'react';
import { ArrayInput, SimpleFormIterator, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ScopedIteratorFields } from '../event-utils/ScopedIteratorFields';
import {
  StandardProductForm as SharedStandardProductForm,
  SeatedProductForm as SharedSeatedProductForm,
} from '../../stocks/StocksProductForm';
import { normalizeEventType } from '../../../utils/eventFormUtils';
import { generateTicketID } from '../../../utils/formatting';
import { useSourceLanguage } from '../../../hooks/useSourceLanguage';
import { SourceLanguageBanner } from '../SourceLanguageBanner';

/** Genel biletler kutusunda birleşen gruplar (davetiye hariç). */
const UNIFIED_TICKET_GROUPS = ['deal', 'guestlist', 'general', 'vip', 'extras'];

const GROUP_LABEL_KEYS = {
  deal: 'form.special_deal',
  guestlist: 'form.reservation',
  general: 'form.general_ticket_type',
  vip: 'form.vip_list',
  extras: 'form.extras',
};

const GROUP_LABEL_FALLBACKS = {
  deal: 'Special Deal',
  guestlist: 'Reservation',
  general: 'General Ticket',
  vip: 'VIP List',
  extras: 'Extras',
};

const PANEL_SX = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  bgcolor: '#fff',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

const PANEL_HEADER_SX = {
  px: 2,
  py: 1.5,
  borderBottom: '1px solid #eef0f3',
  bgcolor: '#fafbfc',
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
  flexWrap: 'wrap',
};

const PRODUCT_CARD_SX = {
  border: '1px solid #e8eaee',
  borderRadius: '10px',
  bgcolor: '#fff',
  p: { xs: 1.5, sm: 2 },
  mb: 1.5,
};

const emptyProduct = () => ({
  id: generateTicketID(),
  status: 0,
  refundable: false,
  hasDownPayment: false,
  hasBasketLimit: false,
  sold: 0,
  price: 0,
  stock: 0,
});

/**
 * Product Form Manager Component
 * Manages product/ticket forms based on event type
 */
export const ProductFormManager = ({
  eventType,
  disabled = false,
  isEdit = false,
  record = {},
}) => {
  const formType = useWatch({ name: 'type' });
  const resolvedType = normalizeEventType(formType ?? eventType);
  const { sourceLang } = useSourceLanguage({ isEdit, record });

  if (resolvedType === 1 || resolvedType === 2) {
    return (
      <StandardProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (resolvedType === 3) {
    return (
      <SeatedProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (eventType === 1 || eventType === 2) {
    return (
      <StandardProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (eventType === 3) {
    return (
      <SeatedProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  return null;
};

/**
 * Standard Product Form — sol: Genel Biletler, sağ: Davetiyeler
 */
const StandardProductForm = ({ disabled = false, sourceLang, record, isEdit }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: { xs: 2, sm: 2.5 },
          width: '100%',
          maxWidth: '100%',
          alignItems: 'stretch',
          boxSizing: 'border-box',
          '& > *': {
            minWidth: 0,
            width: '100%',
            height: '100%',
          },
        }}
      >
        <UnifiedGeneralTicketsSection disabled={disabled} sourceLang={sourceLang} />
        <ProductFormSection
          title={translate('form.invitations', 'Invitations')}
          source="tickets.invitation"
          disabled={disabled}
          sourceLang={sourceLang}
          helperText={translate(
            'form.invitations_hint',
            'Can create invitations for guests to attend the event.',
          )}
        />
      </Box>
    </React.Fragment>
  );
};

/**
 * Seated Product Form Component
 * For Seated events
 */
const SeatedProductForm = ({ disabled = false, sourceLang, record, isEdit }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <ArrayInput source="tickets" label={translate('form.tickets', 'Tickets')} defaultValue={[]}>
        <SimpleFormIterator
          disableReordering
          className="sessionEventTicketsArray"
          disableRemove={disabled}
          disableAdd={disabled}
        >
          <ScopedIteratorFields>
            {({ scopedFormData, getSource }) => (
              <SeatedProductFormFields
                getSource={getSource}
                scopedFormData={scopedFormData}
                disabled={disabled}
                sourceLang={sourceLang}
              />
            )}
          </ScopedIteratorFields>
        </SimpleFormIterator>
      </ArrayInput>
    </React.Fragment>
  );
};

/**
 * deal / guestlist / general / vip / extras → tek "Genel Biletler" kutusu.
 * Artıya basmadan önce tip select ile grup seçilir; yeni ürün her zaman alta eklenir.
 */
const UnifiedGeneralTicketsSection = ({ disabled = false, sourceLang }) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const [addGroup, setAddGroup] = useState('general');
  /** Ekleme sırası (ticket id) — yeni ürün her zaman listenin altına gider. */
  const [displayOrder, setDisplayOrder] = useState([]);

  const deal = useWatch({ name: 'tickets.deal' });
  const guestlist = useWatch({ name: 'tickets.guestlist' });
  const general = useWatch({ name: 'tickets.general' });
  const vip = useWatch({ name: 'tickets.vip' });
  const extras = useWatch({ name: 'tickets.extras' });

  const groupChoices = useMemo(
    () =>
      UNIFIED_TICKET_GROUPS.map((id) => ({
        id,
        name: translate(GROUP_LABEL_KEYS[id], { _: GROUP_LABEL_FALLBACKS[id] }),
      })),
    [translate],
  );

  const locatedItems = useMemo(() => {
    const byGroup = {
      deal: Array.isArray(deal) ? deal : [],
      guestlist: Array.isArray(guestlist) ? guestlist : [],
      general: Array.isArray(general) ? general : [],
      vip: Array.isArray(vip) ? vip : [],
      extras: Array.isArray(extras) ? extras : [],
    };
    const byId = new Map();
    UNIFIED_TICKET_GROUPS.forEach((group) => {
      byGroup[group].forEach((item, index) => {
        const id = item?.id || `${group}-${index}`;
        byId.set(id, { group, index, item, id });
      });
    });
    return byId;
  }, [deal, guestlist, general, vip, extras]);

  const compareOrder = (aId, bId) => {
    const a = Number(locatedItems.get(aId)?.item?.order);
    const b = Number(locatedItems.get(bId)?.item?.order);
    const aHas = Number.isFinite(a);
    const bHas = Number.isFinite(b);
    if (aHas && bHas && a !== b) return a - b;
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  };

  // Bilinen ürünlerle displayOrder'ı senkron tut; ilk yüklemede order alanına göre sırala
  React.useEffect(() => {
    const existingIds = Array.from(locatedItems.keys());
    setDisplayOrder((prev) => {
      if (prev.length === 0) {
        return [...existingIds].sort(compareOrder);
      }
      const kept = prev.filter((id) => locatedItems.has(id));
      const missing = existingIds.filter((id) => !kept.includes(id)).sort(compareOrder);
      if (kept.length === prev.length && missing.length === 0) return prev;
      return [...kept, ...missing];
    });
    // compareOrder depends on locatedItems; intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatedItems]);

  const syncOrderFields = useCallback(
    (orderedIds) => {
      UNIFIED_TICKET_GROUPS.forEach((group) => {
        const list = getValues(`tickets.${group}`);
        if (!Array.isArray(list) || !list.length) return;
        let changed = false;
        const next = list.map((item) => {
          if (!item?.id) return item;
          const orderIndex = orderedIds.indexOf(item.id);
          if (orderIndex < 0 || Number(item.order) === orderIndex) return item;
          changed = true;
          return { ...item, order: orderIndex };
        });
        if (changed) {
          setValue(`tickets.${group}`, next, { shouldDirty: true, shouldTouch: true });
        }
      });
    },
    [getValues, setValue],
  );

  // Panel sırasını ürün.order alanına yaz (sitede buna göre listelenir)
  React.useEffect(() => {
    if (!displayOrder.length) return;
    syncOrderFields(displayOrder);
  }, [displayOrder, syncOrderFields]);

  const flatItems = useMemo(() => {
    const rows = [];
    const seen = new Set();
    displayOrder.forEach((id) => {
      const row = locatedItems.get(id);
      if (row) {
        rows.push(row);
        seen.add(id);
      }
    });
    locatedItems.forEach((row, id) => {
      if (!seen.has(id)) rows.push(row);
    });
    return rows;
  }, [displayOrder, locatedItems]);

  const handleMove = useCallback(
    (itemId, direction) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !itemId) return;
      setDisplayOrder((prev) => {
        const i = prev.indexOf(itemId);
        const j = i + direction;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        const next = [...prev];
        [next[i], next[j]] = [next[j], next[i]];
        return next;
      });
    },
    [disabled],
  );

  const handleAdd = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !addGroup) return;
      const product = emptyProduct();
      const source = `tickets.${addGroup}`;
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.push(product);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
      setDisplayOrder((prev) => {
        const next = [...prev, product.id];
        return next;
      });
    },
    [addGroup, disabled, getValues, setValue],
  );

  const handleRemove = useCallback(
    (group, index, itemId) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      const source = `tickets.${group}`;
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.splice(index, 1);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
      if (itemId) {
        setDisplayOrder((prev) => prev.filter((id) => id !== itemId));
      }
    },
    [disabled, getValues, setValue],
  );

  const handleChangeGroup = useCallback(
    (fromGroup, fromIndex, toGroup) => {
      if (disabled || !toGroup || toGroup === fromGroup) return;
      const fromPath = `tickets.${fromGroup}`;
      const toPath = `tickets.${toGroup}`;
      const fromList = Array.isArray(getValues(fromPath)) ? [...getValues(fromPath)] : [];
      if (fromIndex < 0 || fromIndex >= fromList.length) return;
      const [moved] = fromList.splice(fromIndex, 1);
      const toList = Array.isArray(getValues(toPath)) ? [...getValues(toPath)] : [];
      toList.push(moved);
      setValue(fromPath, fromList, { shouldDirty: true, shouldTouch: true });
      setValue(toPath, toList, { shouldDirty: true, shouldTouch: true });
      // Tip değişince sırayı koru (displayOrder id üzerinden zaten sabit)
    },
    [disabled, getValues, setValue],
  );

  return (
    <Box sx={PANEL_SX}>
      <Box sx={PANEL_HEADER_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ m: 0, color: '#111827' }}>
          {translate('form.general_ticket', 'General Tickets')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b45309', fontStyle: 'italic' }}>
          {translate('form.all_fields_required', '(* all fields required)')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', width: '100%' }}>
          {translate('form.ticket_order_hint', {
            _: 'Yukarı / aşağı ile sitedeki sırayı ayarlayın.',
          })}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
        {flatItems.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              border: '1px dashed #d1d5db',
              borderRadius: '10px',
              bgcolor: '#f9fafb',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {translate('form.no_general_tickets_yet', {
                _: 'Henüz bilet veya ürün eklenmedi. Tip seçip ekleyin.',
              })}
            </Typography>
          </Box>
        ) : (
          flatItems.map(({ group, index, item, id }, listIndex) => {
            const getSource = (field) => `tickets.${group}.${index}.${field}`;
            return (
              <Box key={id} sx={PRODUCT_CARD_SX}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: '8px',
                        bgcolor: '#f3f4f6',
                        color: '#4b5563',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {listIndex + 1}
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: 0, sm: 200 }, flex: 1 }}
                      disabled={disabled}
                    >
                      <InputLabel id={`ticket-group-${id}-label`}>
                        {translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                      </InputLabel>
                      <Select
                        labelId={`ticket-group-${id}-label`}
                        label={translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                        value={group}
                        onChange={(e) => handleChangeGroup(group, index, e.target.value)}
                      >
                        {groupChoices.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  {!disabled && (
                    <Stack
                      direction="row"
                      spacing={0.25}
                      alignItems="center"
                      sx={{ flexShrink: 0 }}
                    >
                      <Tooltip title={translate('form.move_up', { _: 'Yukarı taşı' })}>
                        <span>
                          <IconButton
                            type="button"
                            size="small"
                            onClick={handleMove(id, -1)}
                            disabled={listIndex === 0}
                            aria-label="move-up"
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate('form.move_down', { _: 'Aşağı taşı' })}>
                        <span>
                          <IconButton
                            type="button"
                            size="small"
                            onClick={handleMove(id, 1)}
                            disabled={listIndex === flatItems.length - 1}
                            aria-label="move-down"
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate('ra.action.delete', { _: 'Sil' })}>
                        <IconButton
                          type="button"
                          size="small"
                          onClick={handleRemove(group, index, id)}
                          aria-label="remove"
                          sx={{
                            color: '#9ca3af',
                            '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.06)' },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
                <StandardProductFormFields
                  getSource={getSource}
                  scopedFormData={item || {}}
                  disabled={disabled}
                  sourceLang={sourceLang}
                />
              </Box>
            );
          })
        )}

        {!disabled && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              pt: 1.5,
              mt: flatItems.length ? 0.5 : 0,
              borderTop: flatItems.length ? '1px solid #eef0f3' : 'none',
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: '1 1 200px' } }}
            >
              <InputLabel id="unified-ticket-add-type-label">
                {translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
              </InputLabel>
              <Select
                labelId="unified-ticket-add-type-label"
                label={translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                value={addGroup}
                onChange={(e) => setAddGroup(e.target.value)}
              >
                {groupChoices.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="button"
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAdd}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                borderRadius: '10px',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {translate('form.add_ticket_product', { _: 'Ürün Ekle' })}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Product Form Section Component
 * Individual product form section with title and form fields
 */
const ProductFormSection = ({ title, source, disabled = false, helperText = null, sourceLang }) => {
  const translate = useTranslate();
  return (
    <Box sx={PANEL_SX}>
      <Box sx={PANEL_HEADER_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ m: 0, color: '#111827' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b45309', fontStyle: 'italic' }}>
          {translate('form.all_fields_required', '(* all fields required)')}
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
        {helperText ? (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              px: 1.25,
              py: 0.75,
              borderRadius: '8px',
              bgcolor: '#0ABAB5',
              color: '#fff',
              display: 'inline-block',
              lineHeight: 1.4,
            }}
          >
            {helperText}
          </Typography>
        ) : null}
        <ArrayInput source={source} label={false} defaultValue={[]}>
          <SimpleFormIterator disableRemove={disabled} disableAdd={disabled}>
            <ScopedIteratorFields>
              {({ scopedFormData, getSource }) => (
                <Box sx={{ ...PRODUCT_CARD_SX, mb: 0 }}>
                  <StandardProductFormFields
                    getSource={getSource}
                    scopedFormData={scopedFormData}
                    disabled={disabled}
                    sourceLang={sourceLang}
                  />
                </Box>
              )}
            </ScopedIteratorFields>
          </SimpleFormIterator>
        </ArrayInput>
      </Box>
    </Box>
  );
};

const StandardProductFormFields = ({ getSource, scopedFormData, disabled = false, sourceLang }) => {
  return (
    <SharedStandardProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={false}
      disabled={disabled}
      sourceLang={sourceLang}
    />
  );
};

const SeatedProductFormFields = ({ getSource, scopedFormData, disabled = false, sourceLang }) => {
  return (
    <SharedSeatedProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={false}
      disabled={disabled}
      sourceLang={sourceLang}
    />
  );
};
